import { useSession } from '@repo/api-client/react';
import { Mail, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { toAppError } from '@repo/api-client';
import { AuthLayout } from './AuthLayout';

export function VerifyRequiredScreen() {
  const { user, resendEmailVerification, refreshEmailVerified, signOut } = useSession();
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  return (
    <AuthLayout
      title="Verify your email"
      subtitle={
        <>
          We sent a confirmation link to{' '}
          <span className="font-medium text-foreground">{user?.email}</span>. Click
          the link to finish setting up your studio.
        </>
      }
      footer={
        <Button
          variant="link"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={() => {
            void signOut();
          }}
        >
          Sign out
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-lg border border-line bg-secondary/50 px-4 py-3">
          <Mail className="size-5 text-accent" />
          <div className="text-sm leading-relaxed text-muted-foreground">
            Didn't receive it? Check your spam folder, or send the email again.
          </div>
        </div>

        <Button
          variant="primary"
          loading={refreshing}
          leadingIcon={<RefreshCw className="size-4" />}
          onClick={async () => {
            setRefreshing(true);
            try {
              await refreshEmailVerified();
              toast({
                variant: 'info',
                title: 'Status refreshed',
                description: 'If you verified, the dashboard will load shortly.',
              });
            } finally {
              setRefreshing(false);
            }
          }}
        >
          I verified my email
        </Button>

        <Button
          variant="outline"
          loading={sending}
          onClick={async () => {
            setSending(true);
            try {
              const result = await resendEmailVerification();
              if (result.alreadyVerified) {
                toast({
                  variant: 'success',
                  title: 'Already verified',
                  description: 'Refreshing your session…',
                });
                await refreshEmailVerified();
              } else {
                toast({
                  variant: 'success',
                  title: 'Email sent',
                  description: 'Check your inbox for the new link.',
                });
              }
            } catch (err) {
              const copy = describeAppError(toAppError(err));
              toast({
                variant: 'danger',
                title: copy.title,
                description: copy.message,
              });
            } finally {
              setSending(false);
            }
          }}
        >
          Resend verification email
        </Button>
      </div>
    </AuthLayout>
  );
}
